/** @jsx jsx */
import React from "react";
import { css, jsx } from "@emotion/core";
import { useState, useEffect, useRef, useCallback } from "react";
import { message, Input, Icon, Button, Select } from "antd";
import DateRangePicker from "../../_shared/date-range-picker";
import styles from "./styles";
import moment from "moment";
import LNSearch from "../../_shared/ln-search";
import { get, size } from "lodash";
import { getReconList, mergePdfs, deleteReconciliation } from "../../_shared/services/orders.service";
import { TableList } from "../../_shared/table-list";
import {
  getCopiesUrl,
  getRawLicense,
  getReconUrl,
  rowMatchesSearch,
  searchArray,
  sortableDate,
  stopProp,
  userHasRole,
  withSorters,
} from "../../actions/utils";
import { Modal as AntModal } from "antd";
import { deliveryStatus } from "../../models/order";
import { useSelector } from "react-redux";
import { OrderModal } from "../orders/orders.component";

export const EditRow = (t, row, key, props) => {
  const hasRecon = get(row, "data.recon");
  if (hasRecon && !props.forceRender) {
    return t;
  }

  return (
    <Input
      disabled={props.disabled}
      onClick={stopProp}
      value={t}
      title={t}
      loading={props.saving}
      onChange={(e) => props.onChangeText(e.target.value, row, key)}
    />
  );
};

// Card #240 (Danielle 2026-05-26 mockup) — formats a date or empty string.
const fmtDateOrEmpty = (d) => (d ? moment(d).format("L") : "");

export const ReconList = (props) => {
  const user = useSelector((state) => get(state, "user"));
  const isAdminScreen = window.location.href.includes('reconlistadmin');
  const [state, setState] = useState({
    loading: false,
    saving: false,
    selectedDate: [moment().subtract(2, "weeks").startOf("day"), moment().add(2, "weeks").endOf("day")],
    vendor: undefined,
    text: "",
    data: [],
    filteredData: null,
    editModal: undefined,
    selectedRows: [],
    merging: false,
  });
  // Card #240 (Round 3 — Joel 2026-06-02 Loom) — All / Customer Pickup /
  // Returns dropdown filter. Default = "all" per the card spec. Each row is
  // a RECONCILIATION (one row per recon). The filter operates on the recon
  // row's return-order-derived flags (customerPickup / orderType), which the
  // server attaches to every recon row.
  //   'all'             = no filter
  //   'customerPickup'  = rows where customerPickup === true
  //   'returns'         = rows where orderType === 'cash_return'
  const [reconTypeFilter, setReconTypeFilter] = useState('all');
  const LNRef = useRef();

  useEffect(() => {
    if (state.selectedDate && !size(state.selectedDate)) {
      setState(s => ({ ...s, selectedDate: null }));
      return;
    }
    retrieveData();
  }, [state.selectedDate, state.vendor]);

  const onSearch = useCallback((t = state.text) => {
    if (!t) {
      setState((s) => ({ ...s, filteredData: null }));
      return;
    }
    // Card #240 (Round 3) — rows are reconciliations. Search must still match
    // a recon by any of its UNDERLYING orders' fields even though those
    // orders are not listed individually. The server attaches a hidden
    // `searchData` blob (orderIds, invoices, and per-order licensee/DBA/LN).
    // searchArray JSON-stringifies object values, so pointing a key at
    // `searchData` matches any nested order id / invoice / license / DBA.
    const byKeys = searchArray(state.data, {
      searchTerm: t,
      keys: [
        "reconId",
        "reconInvoice",
        "reconLicense",
        "returnOrderId",
        "pickupLicensee",
        "destLicensee",
        "destDBA",
        "searchData",
      ],
    });

    // Card #277 — the keys above reach INTO each reconciliation (including the hidden
    // searchData blob) and are kept for that reason, but they miss what the grid puts on
    // screen: none of these columns has a dataIndex, so the return date, the status behind
    // its icon and the created date are all produced by the renderers alone. Matching the
    // rendered cells as well makes the two agree with each other.
    const matched = new Set(byKeys);
    // No selectOrder: it only wires the Order ID cell's click handler, and the text the
    // cell shows is the same either way — leaving it out keeps this callback's
    // dependencies to values that exist before it is declared.
    const cols = getCols({ ...state, isAdminScreen, user });

    const filteredData = state.data.filter(
      r => matched.has(r) || rowMatchesSearch(cols, r, t, { useRender: true })
    );

    setState((s) => ({ ...s, filteredData }));
  }, [state, isAdminScreen, user]);

  // Card #240 (Round 3) — applies the All / Customer Pickup / Returns
  // dropdown filter on top of the search-filtered (or unfiltered) data.
  // Rows are reconciliations; each carries the return-order-derived
  // customerPickup flag and orderType from the server.
  const applyTypeFilter = useCallback((rows = []) => {
    if (reconTypeFilter === 'all') return rows;
    if (reconTypeFilter === 'customerPickup') {
      return rows.filter(r => !!get(r, 'customerPickup'));
    }
    if (reconTypeFilter === 'returns') {
      return rows.filter(r => get(r, 'orderType') === 'cash_return');
    }
    return rows;
  }, [reconTypeFilter]);

  const retrieveData = async () => {
    try {
        if (!isAdminScreen && !state.vendor) {
          return;
        }

        setState((s) => ({ ...s, loading: true }));

        const data = await getReconList({
            selectedDate: state.selectedDate,
            vendor: state.vendor,
        });

        const vendorProps = getRawLicense(get(state, "vendor.License #"));

        setState((s) => ({ ...s, data, vendorProps }));
    } catch (err) {
      message.error(err.message);
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const selectDate = (date) => {
    setState((s) => ({ ...s, selectedDate: date }));
  };

  const handleVendorSelect = (v) => {
    const vendor = get(LNRef, "current.state.licenseNumbersAll", []).find(
      (l) => get(l, "License #") === v
    );
    setState((s) => ({ ...s, vendor }));
  };

  const onLoaded = (st) => {
    if (!isAdminScreen) {
        const vendor = st.allLns.find(ln => get(ln, 'License #') == get(user, 'company.License #'));
        setState(s => ({ ...s, vendor }))
    }
  };

  const changeSearch = useCallback((e) => {
    const text = e.target.value;
    setState((s) => ({ ...s, text }));
  }, []);

  // Card #240 (Round 3) — the Order ID column shows the recon's single
  // return order; clicking it (Admin/Super Admin only) opens that order in
  // the modal.
  const selectOrder = (row) => {
    const editOrder = get(row, 'returnOrder') || {
      id: get(row, 'returnOrderDbId'),
      orderId: get(row, 'returnOrderId'),
    };
    const orderType = get(editOrder, 'data.order.orderType') || get(row, 'orderType');
    const cashReturn = orderType === 'cash_return' || orderType === 'cash_pickup';
    setState(s => ({
      ...s,
      editModal: {
        isModal: true,
        state: {
          cashReturn,
          editOrder,
        }
      }
    }));
  };

  const onCloseEditModal = () => {
    setState(s => ({ ...s, editModal: undefined }));
  };

  const onSelectedRowsChange = (keys) => {
    setState(s => ({ ...s, selectedRows: keys }));
  };

  // Card #240 (Round 3) — selected rows are reconciliations. Each row's id
  // is the reconId; list the underlying order ids (from searchData) in the
  // confirm dialog.
  const onDeleteSelected = () => {
    const data = state.filteredData !== null ? state.filteredData : state.data;
    const selected = data.filter(d => state.selectedRows.includes(d.id));
    const reconIds = Array.from(new Set(selected.map(r => r.reconId || r.id).filter(Boolean)));
    const orderIds = selected.flatMap(r => get(r, 'searchData.orderIds', [])).filter(Boolean);

    AntModal.confirm({
      title: 'Delete Reconciliation',
      content: `Are you sure you want to delete the reconciliation(s) ${reconIds.join(', ')}? This will remove the following orders from the reconciliation: ${orderIds.join(', ')}.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteReconciliation(reconIds);
          message.success('Reconciliation(s) deleted successfully');
          setState(s => ({ ...s, selectedRows: [] }));
          retrieveData();
        } catch (err) {
          message.error('Failed to delete: ' + err.message);
        }
      },
    });
  };

  // Card #240 — bulk print operates on the recon PDFs of the selected
  // orders (de-duplicated, since multiple selected orders can share a
  // single reconciliation).
  const onMergePrint = async () => {
    const data = state.filteredData !== null ? state.filteredData : state.data;
    const selected = data.filter(d => state.selectedRows.includes(d.id));
    const files = Array.from(new Set(selected.map(r => r.reconFile).filter(Boolean)));
    const pdfs = files.map(file => `https://gps-one-uploads.s3.us-west-2.amazonaws.com/${file}`);

    if (!size(pdfs)) {
      message.warning('No PDFs found for selected rows');
      return;
    }

    try {
      setState(s => ({ ...s, merging: true }));
      const resp = await mergePdfs({ pdfs });
      const filename = get(resp, 'data', resp);
      const url = getCopiesUrl(filename);
      window.open(url, '_blank');
    } catch (err) {
      message.error('Failed to merge PDFs: ' + err.message);
    } finally {
      setState(s => ({ ...s, merging: false }));
    }
  };

  // Card #240 — clicking a row opens the recon PDF.
  const goToRecon = (row) => {
    const url = getReconUrl(get(row, 'reconFile') || get(row, 'data.recon.file'));
    if (!url) return;
    window.open(url, "_blank");
  };

  useEffect(() => {
    onSearch();
  }, [state.data]);

  return (
    <div css={css(styles.container)}>
      <div className="no-print" css={css(styles.calContainer)}>
        <div css={css(styles.input, styles.row)}>
          <Input.Search
            placeholder={"Search all columns..."}
            enterButton
            onSearch={onSearch}
            value={state.text}
            onChange={changeSearch}
          />
          <LNSearch
            onLoaded={onLoaded}
            ref={LNRef}
            onSelect={handleVendorSelect}
            placeholder={"License Number Select"}
            style={{ display: isAdminScreen ? 'block' : 'none' }}
          />
          <DateRangePicker
            css={css(styles.input)}
            loading={state.loading}
            value={state.selectedDate}
            onChange={selectDate}
          />
          {/* Card #240 — All / Customer Pickup / Returns filter (mockup). */}
          <Select
            value={reconTypeFilter}
            onChange={setReconTypeFilter}
            style={{ minWidth: 160 }}
            aria-label="Reconciliation type filter"
          >
            <Select.Option value="all">All</Select.Option>
            <Select.Option value="customerPickup">Customer Pickup</Select.Option>
            <Select.Option value="returns">Returns</Select.Option>
          </Select>
          {size(state.selectedRows) > 0 && (
            <>
              <Button
                type="primary"
                icon="printer"
                loading={state.merging}
                onClick={onMergePrint}
              >
                {`Print Selected (${size(state.selectedRows)})`}
              </Button>
              {userHasRole([0, 1], { user }) && (
                <Button
                  type="danger"
                  icon="delete"
                  onClick={onDeleteSelected}
                  style={{ marginLeft: 8 }}
                >
                  {`Delete Selected (${size(state.selectedRows)})`}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="no-print">
        <TableList
          rowClick={goToRecon}
          canSelectRows={true}
          loading={state.loading}
          pageSize={250}
          data={applyTypeFilter(state.filteredData !== null ? state.filteredData : state.data)}
          columns={withSorters(getCols({ ...state, selectOrder, isAdminScreen, user }))}
          onRefresh={retrieveData}
          onSelectedRowsChange={onSelectedRowsChange}
          selectedRowKeys={state.selectedRows}
        />
      </div>
      <OrderModal editModal={state.editModal} onClose={onCloseEditModal} />
    </div>
  );
};

// Card #240 (Round 3 — Joel 2026-06-02 Loom + Danielle mockup) — column
// definitions per the posted mockup. The table is ONE ROW PER
// RECONCILIATION (the Round-2 row-per-order rebuild is reverted). Layout:
//   Return Date | Order ID | Recon ID | Pickup | Destination | DBA | Status | Created At
// where, per the mockup + Loom:
//   - Order ID    = the recon's single return order id (one per recon),
//                   clickable for Admin (1) / Super Admin (0), plain text
//                   for Account Admin (3) / Standard User (4).
//   - Pickup      = the carrier (GPS Transportation) — the return order's
//                   pickupCompany.
//   - Destination = the main license/account under the recon — the return
//                   order's destCompany.
//   - Return Date = the return order's delivery date ("Customer Pickup"
//                   when the return is flagged customerPickup).
//
// Card #277 round 3 — sorting. No column here has a dataIndex, so there is no field for the
// shared sorter to read and each column's `sortValue` says what its cell shows.
const getCols = (props) => {
  const canClickOrderId = userHasRole([0, 1], { user: props.user });

  return [
    {
      title: 'Return Date',
      key: 'returnDate',
      width: '10%',
      // By date. A customer pickup has no return date, so those rows sort together after
      // the dated returns.
      sortValue: row => (get(row, 'customerPickup') ? 'CUSTOMER PICKUP' : sortableDate(get(row, 'deliveryDate'))),
      render: (_, row) => {
        // "Customer Pickup" displaces the date when the recon's return is
        // flagged customerPickup — no return route/driver/date is scheduled.
        if (get(row, 'customerPickup')) return 'CUSTOMER PICKUP';
        return fmtDateOrEmpty(get(row, 'deliveryDate'));
      },
    },
    {
      title: 'Order ID',
      key: 'orderId',
      width: '12%',
      // "No return order" is a placeholder for a missing id, so it sorts with the blanks.
      sortValue: row => get(row, 'returnOrderId'),
      render: (_, row) => {
        const orderId = get(row, 'returnOrderId');
        // Card #285 — a recon can exist with no Reconciliation Return order:
        // "Export Rec" creates the recon row before the return order, so
        // backing out of the Create Order modal used to leave one orphaned
        // (prevented going forward in reconciliation/index.js, but historical
        // rows remain). Render a muted, non-clickable placeholder instead of an
        // empty cell so the row reads as intentional.
        if (!orderId) {
          return <span css={css(styles.mutedCell)}>{'No return order'}</span>;
        }
        if (!canClickOrderId) {
          return <span>{orderId}</span>;
        }
        return (
          <a
            onClick={(e) => {
              e.stopPropagation();
              props.selectOrder(row);
            }}
          >
            {orderId}
          </a>
        );
      },
    },
    {
      title: 'Recon ID',
      key: 'reconId',
      width: '8%',
      // The cell is a "Link" to the recon PDF; the id behind it is a guid that is never
      // shown. So it sorts on whether there is a PDF to link to.
      sortValue: row => (getReconUrl(get(row, 'reconFile') || get(row, 'data.recon.file')) ? 1 : null),
      render: (_, row) => {
        const url = getReconUrl(get(row, 'reconFile') || get(row, 'data.recon.file'));
        if (!url) return '';
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            Link
          </a>
        );
      },
    },
    {
      title: 'Pickup',
      key: 'pickup',
      width: '15%',
      sortValue: row => get(row, 'pickupLicensee'),
      render: (_, row) => get(row, 'pickupLicensee') || '',
    },
    {
      title: 'Destination',
      key: 'destName',
      width: '15%',
      sortValue: row => get(row, 'destLicensee'),
      render: (_, row) => get(row, 'destLicensee') || '',
    },
    {
      title: 'DBA',
      key: 'dba',
      width: '15%',
      sortValue: row => get(row, 'destDBA'),
      render: (_, row) => get(row, 'destDBA') || '',
    },
    {
      title: 'Status',
      key: 'status',
      width: '8%',
      // The icon's label (its hover title, and what the search matches), alphabetically.
      sortValue: row => get(deliveryStatus.find(d => d.value == row.status), 'title'),
      render: (_, row) => {
        const stat = deliveryStatus.find(d => d.value == row.status);
        if (!stat || !stat.icon) return '';
        return (
          <Icon
            type={stat.icon}
            title={stat.title}
            css={css(`color: ${stat.color};`)}
          />
        );
      },
    },
    {
      title: 'Created At',
      key: 'createdAt',
      width: '10%',
      sortValue: row => sortableDate(row.reconCreatedAt || row.createdAt),
      render: (_, row) => fmtDateOrEmpty(row.reconCreatedAt || row.createdAt),
    },
  ];
};

// Card #240 (Round 3) — Export the type-filter predicate so it can be
// unit-tested in AUTOTESTING/240/filter-test.js without booting the whole
// app. Rows are reconciliations carrying return-order-derived flags.
export const filterReconsByType = (rows = [], filterValue = 'all') => {
  if (filterValue === 'all') return rows;
  if (filterValue === 'customerPickup') {
    return rows.filter(r => !!get(r, 'customerPickup'));
  }
  if (filterValue === 'returns') {
    return rows.filter(r => get(r, 'orderType') === 'cash_return');
  }
  return rows;
};
